// ---------------------------------------------------------------------------
// 32x32.png — shared 96x96 spritesheet (3 animations on 3 rows)
// ---------------------------------------------------------------------------

import { ISpritesheetData } from 'pixi.js';
import { ISpritesheetMetadata } from '../../types/types';

// Horizontal strip of square frames (side = image height). Idle shows frame 0;
// on activation the renderer plays through once and rests on the last frame
// (cf. LocationRenderer + triggerPoiBreakAnimation).
const trapStrip = (
  name: string,
  image: string,
  cell: number,
  count: number,
): ISpritesheetMetadata => {
  const frames: ISpritesheetData['frames'] = {};
  const anim: string[] = [];
  for (let i = 0; i < count; i++) {
    const key = `${name}-${i}`;
    frames[key] = {
      frame: { x: i * cell, y: 0, w: cell, h: cell },
      sourceSize: { w: cell, h: cell },
      spriteSourceSize: { x: 0, y: 0, w: cell, h: cell },
    };
    anim.push(key);
  }
  return {
    name,
    metadata: {
      frames,
      meta: { image, size: { w: count * cell, h: cell }, scale: '1' },
      animations: { [name]: anim },
    },
  };
};

export const LocationSpritesheets: ISpritesheetMetadata[] = [
  {
    name: 'caisse',
    metadata: {
      frames: {
        'caisse-0': {
          frame: { x: 0, y: 0, w: 32, h: 32 },
          sourceSize: { w: 32, h: 32 },
          spriteSourceSize: { x: 0, y: 0, w: 32, h: 32 },
        },
        'caisse-1': {
          frame: { x: 32, y: 0, w: 32, h: 32 },
          sourceSize: { w: 32, h: 32 },
          spriteSourceSize: { x: 0, y: 0, w: 32, h: 32 },
        },
        'caisse-2': {
          frame: { x: 64, y: 0, w: 32, h: 32 },
          sourceSize: { w: 32, h: 32 },
          spriteSourceSize: { x: 0, y: 0, w: 32, h: 32 },
        },
      },
      meta: {
        image: '/sprites/structure/32x32.png',
        size: { w: 96, h: 96 },
        scale: '1',
      },
      animations: {
        caisse: ['caisse-0', 'caisse-1', 'caisse-2'],
      },
    },
  },
  {
    name: 'urne',
    metadata: {
      frames: {
        'urne-0': {
          frame: { x: 0, y: 32, w: 32, h: 32 },
          sourceSize: { w: 32, h: 32 },
          spriteSourceSize: { x: 0, y: 0, w: 32, h: 32 },
        },
        'urne-1': {
          frame: { x: 32, y: 32, w: 32, h: 32 },
          sourceSize: { w: 32, h: 32 },
          spriteSourceSize: { x: 0, y: 0, w: 32, h: 32 },
        },
        'urne-2': {
          frame: { x: 64, y: 32, w: 32, h: 32 },
          sourceSize: { w: 32, h: 32 },
          spriteSourceSize: { x: 0, y: 0, w: 32, h: 32 },
        },
      },
      meta: {
        image: '/sprites/structure/32x32.png',
        size: { w: 96, h: 96 },
        scale: '1',
      },
      animations: {
        urne: ['urne-0', 'urne-1', 'urne-2'],
      },
    },
  },
  {
    name: 'coffre',
    metadata: {
      frames: {
        'coffre-0': {
          frame: { x: 0, y: 64, w: 32, h: 32 },
          sourceSize: { w: 32, h: 32 },
          spriteSourceSize: { x: 0, y: 0, w: 32, h: 32 },
        },
        'coffre-1': {
          frame: { x: 32, y: 64, w: 32, h: 32 },
          sourceSize: { w: 32, h: 32 },
          spriteSourceSize: { x: 0, y: 0, w: 32, h: 32 },
        },
      },
      meta: {
        image: '/sprites/structure/32x32.png',
        size: { w: 96, h: 96 },
        scale: '1',
      },
      animations: {
        coffre: ['coffre-0', 'coffre-1'],
      },
    },
  },
  {
    name: 'fountain',
    metadata: {
      frames: {
        'fountain-0': {
          frame: { x: 0, y: 0, w: 128, h: 128 },
          sourceSize: { w: 128, h: 128 },
          spriteSourceSize: { x: 0, y: 0, w: 128, h: 128 },
        },
      },
      meta: {
        image: '/sprites/structure/fountain.png',
        size: { w: 128, h: 128 },
        scale: '1',
      },
      animations: {
        fountain: ['fountain-0'],
      },
    },
  },
  {
    name: 'luminous-wall-bottom',
    metadata: {
      frames: {
        'luminous-wall-bottom-0': {
          frame: { x: 0, y: 0, w: 96, h: 48 },
          sourceSize: { w: 96, h: 48 },
          spriteSourceSize: { x: 0, y: 0, w: 96, h: 48 },
        },
      },
      meta: {
        image: '/sprites/structure/luminous-wall-bottom.png',
        size: { w: 96, h: 48 },
        scale: '1',
      },
      animations: {
        'luminous-wall-bottom': ['luminous-wall-bottom-0'],
      },
    },
  },
  {
    name: 'luminous-wall-left',
    metadata: {
      frames: {
        'luminous-wall-left-0': {
          frame: { x: 0, y: 0, w: 48, h: 96 },
          sourceSize: { w: 48, h: 96 },
          spriteSourceSize: { x: 0, y: 0, w: 48, h: 96 },
        },
      },
      meta: {
        image: '/sprites/structure/luminous-wall-left.png',
        size: { w: 48, h: 96 },
        scale: '1',
      },
      animations: {
        'luminous-wall-left': ['luminous-wall-left-0'],
      },
    },
  },
  {
    name: 'luminous-wall-right',
    metadata: {
      frames: {
        'luminous-wall-right-0': {
          frame: { x: 0, y: 0, w: 48, h: 96 },
          sourceSize: { w: 48, h: 96 },
          spriteSourceSize: { x: 0, y: 0, w: 48, h: 96 },
        },
      },
      meta: {
        image: '/sprites/structure/luminous-wall-right.png',
        size: { w: 48, h: 96 },
        scale: '1',
      },
      animations: {
        'luminous-wall-right': ['luminous-wall-right-0'],
      },
    },
  },
  {
    name: 'luminous-wall-top',
    metadata: {
      frames: {
        'luminous-wall-top-0': {
          frame: { x: 0, y: 0, w: 96, h: 48 },
          sourceSize: { w: 96, h: 48 },
          spriteSourceSize: { x: 0, y: 0, w: 96, h: 48 },
        },
      },
      meta: {
        image: '/sprites/structure/luminous-wall-top.png',
        size: { w: 96, h: 48 },
        scale: '1',
      },
      animations: {
        'luminous-wall-top': ['luminous-wall-top-0'],
      },
    },
  },
  {
    name: 'mind-trap',
    metadata: {
      frames: {
        'mind-trap-0': {
          frame: { x: 0, y: 0, w: 256, h: 256 },
          sourceSize: { w: 256, h: 256 },
          spriteSourceSize: { x: 0, y: 0, w: 256, h: 256 },
        },
      },
      meta: {
        image: '/sprites/structure/mind-trap.png',
        size: { w: 256, h: 256 },
        scale: '1',
      },
      animations: {
        'mind-trap': ['mind-trap-0'],
      },
    },
  },
  {
    name: 'nexus',
    metadata: {
      frames: {
        'nexus-0': {
          frame: { x: 0, y: 0, w: 256, h: 256 },
          sourceSize: { w: 256, h: 256 },
          spriteSourceSize: { x: 0, y: 0, w: 256, h: 256 },
        },
      },
      meta: {
        image: '/sprites/structure/nexus.png',
        size: { w: 256, h: 256 },
        scale: '1',
      },
      animations: {
        nexus: ['nexus-0'],
      },
    },
  },
  // Aliases for locations that reuse existing images
  {
    name: 'autel_guide',
    metadata: {
      frames: {
        'autel_guide-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        autel_guide: ['autel_guide-0'],
      },
    },
  },
  {
    name: 'source_onirique',
    metadata: {
      frames: {
        'source_onirique-0': {
          frame: { x: 0, y: 0, w: 128, h: 128 },
          sourceSize: { w: 128, h: 128 },
          spriteSourceSize: { x: 0, y: 0, w: 128, h: 128 },
        },
      },
      meta: {
        image: '/sprites/structure/fountain.png',
        size: { w: 128, h: 128 },
        scale: '1',
      },
      animations: {
        source_onirique: ['source_onirique-0'],
      },
    },
  },
  {
    name: 'stairs',
    metadata: {
      frames: {
        'stairs-0': {
          frame: { x: 0, y: 0, w: 208, h: 208 },
          sourceSize: { w: 208, h: 208 },
          spriteSourceSize: { x: 0, y: 0, w: 208, h: 208 },
        },
      },
      meta: {
        image: '/sprites/structure/stairs.png',
        size: { w: 208, h: 208 },
        scale: '1',
      },
      animations: {
        stairs: ['stairs-0'],
      },
    },
  },
  // B8 placeholders — reuse existing assets pending final art
  // (Grimoire Sanctuary / Memory Stone / Wandering Merchant).
  {
    name: 'grimoire_sanctuary',
    metadata: {
      frames: {
        'grimoire_sanctuary-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        grimoire_sanctuary: ['grimoire_sanctuary-0'],
      },
    },
  },
  {
    name: 'wandering_merchant',
    metadata: {
      frames: {
        'wandering_merchant-0': {
          frame: { x: 0, y: 0, w: 208, h: 156 },
          sourceSize: { w: 208, h: 156 },
          spriteSourceSize: { x: 0, y: 0, w: 208, h: 156 },
        },
      },
      meta: {
        image: '/sprites/structure/wanderer.png',
        size: { w: 208, h: 156 },
        scale: '1',
      },
      animations: {
        wandering_merchant: ['wandering_merchant-0'],
      },
    },
  },
  // B8 — Mini-boss lairs: placeholders reusing shrine.png
  // pending dedicated final art for each biome.
  {
    name: 'lair_boar',
    metadata: {
      frames: {
        'lair_boar-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        lair_boar: ['lair_boar-0'],
      },
    },
  },
  {
    name: 'lair_carp',
    metadata: {
      frames: {
        'lair_carp-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        lair_carp: ['lair_carp-0'],
      },
    },
  },
  {
    name: 'lair_fox',
    metadata: {
      frames: {
        'lair_fox-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        lair_fox: ['lair_fox-0'],
      },
    },
  },
  {
    name: 'lair_owl',
    metadata: {
      frames: {
        'lair_owl-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        lair_owl: ['lair_owl-0'],
      },
    },
  },
  {
    name: 'lair_ibex',
    metadata: {
      frames: {
        'lair_ibex-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        lair_ibex: ['lair_ibex-0'],
      },
    },
  },
  {
    name: 'lair_eel',
    metadata: {
      frames: {
        'lair_eel-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        lair_eel: ['lair_eel-0'],
      },
    },
  },
  {
    name: 'lair_toad',
    metadata: {
      frames: {
        'lair_toad-0': {
          frame: { x: 0, y: 0, w: 86, h: 107 },
          sourceSize: { w: 86, h: 107 },
          spriteSourceSize: { x: 0, y: 0, w: 86, h: 107 },
        },
      },
      meta: {
        image: '/sprites/structure/shrine.png',
        size: { w: 86, h: 107 },
        scale: '1',
      },
      animations: {
        lair_toad: ['lair_toad-0'],
      },
    },
  },
  trapStrip('spike-trap', '/sprites/structure/spike-trap.png', 32, 14),
  trapStrip('slow-trap', '/sprites/structure/slow-trap.png', 48, 2),
  trapStrip('warp-trap', '/sprites/structure/warp-trap.png', 48, 2),
  trapStrip('alarm-trap', '/sprites/structure/alarm-trap.png', 300, 2),
  // Structure POIs with an on/off (unused/used) pair, combined into a 2-frame
  // strip: frame 0 = `-on` (intact, shown idle), frame 1 = `-off` (used, shown
  // after the break animation or on reload of an already-used instance). Same
  // mechanic as the urn/chest (cf. LocationRenderer + triggerPoiBreakAnimation).
  trapStrip('build1', '/sprites/structure/build1.png', 128, 2),
  trapStrip('build2', '/sprites/structure/build2.png', 128, 2),
  trapStrip('circle', '/sprites/structure/circle.png', 128, 2),
  trapStrip('shrine', '/sprites/structure/shrine-poi.png', 128, 2),
];
